import { useQuery } from '@tanstack/react-query';
import {
  Autocomplete,
  FormField,
  Link,
  formFieldRequiredLabel as requiredLabel,
} from '@elementary/common-ui/components';
import { JIRA_FIELD_AUTOCOMPLETE } from '@elementary/common-ui/constants/queryKeys';
import { useFormContext } from 'react-hook-form';
import { Tooltip, Typography } from '@mui/material';
import { fetchJiraFieldAutocomplete } from '../api';
import type { ProjectField } from '../types';
import { JIRA_ISSUE_TYPE_VARIANTS } from './constants';

type Props = {
  field: ProjectField;
  label: string;
  defaultValue: any;
  integrationId: string;
  selectedEnvId: string;
  query: string;
  onInputChange: (value: string) => void;
  onClick: (e: React.MouseEvent) => void;
  projectSettingsUrl?: string | null;
  optional?: boolean;
  errorMessage?: string;
  projectId?: string;
  issueType?: string;
};

const JiraDynamicFieldAutocomplete = ({
  field,
  label,
  defaultValue,
  integrationId,
  selectedEnvId,
  query,
  onInputChange,
  onClick,
  projectSettingsUrl,
  optional = false,
  errorMessage,
  projectId,
  issueType,
}: Props) => {
  const formMethods = useFormContext<Record<string, any>>();
  const { setValue, watch, register } = formMethods;

  register(field.key, {
    required: !optional ? requiredLabel : false,
  });
  const fieldValue = watch(field.key);

  const { data, isLoading } = useQuery({
    queryKey: [
      JIRA_FIELD_AUTOCOMPLETE,
      selectedEnvId,
      integrationId,
      field.key,
      query,
      projectId,
      issueType,
    ],
    queryFn: () =>
      fetchJiraFieldAutocomplete(
        selectedEnvId,
        integrationId,
        field.auto_complete_url!,
        query,
        projectId,
        issueType,
      ),
    enabled:
      !!selectedEnvId &&
      !!field.auto_complete_url &&
      !field.disable_auto_complete,
    select: (data) => data.suggestions,
  });

  const suggestions = data || [];

  const handleInputChange = (
    _: any,
    value: string,
    reason: 'input' | 'reset' | 'clear',
  ) => {
    if (reason === 'input') {
      onInputChange(value);
    }
  };

  const getEmptyMessage = () => {
    if (query && query.trim().length > 0) {
      return <Typography variant="body2">No options found</Typography>;
    }
    return (
      <Typography variant="body2">
        No options found, try checking your{' '}
        {projectSettingsUrl ? (
          <Link
            href={projectSettingsUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            project configuration
          </Link>
        ) : (
          'project configuration'
        )}{' '}
        and configuring allowed values for this field.
      </Typography>
    );
  };

  const disabled =
    (!suggestions || !suggestions?.length) &&
    field.schema_info.items !== 'string' &&
    !fieldValue &&
    !query &&
    !isLoading;

  const tooltipContent = disabled ? (
    <span>
      {`We couldn't find any ${label.toLowerCase()} for this space. Check
          your `}
      {projectSettingsUrl ? (
        <Link
          href={projectSettingsUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          project configuration
        </Link>
      ) : (
        'project configuration'
      )}
      .
    </span>
  ) : (
    ''
  );

  if (field.disable_auto_complete) {
    return null;
  }

  const isParentField = field.key === 'parent';
  const isStory = issueType
    ? JIRA_ISSUE_TYPE_VARIANTS.Story.includes(issueType.toLowerCase())
    : false;
  const description = isParentField
    ? isStory
      ? 'Notice: Only Epics are shown as they are the only valid parent for "Stories"'
      : 'Notice: Epics and Stories are shown as valid parents for "Tasks" and "Bugs"'
    : undefined;

  return (
    <FormField
      key={field.key}
      title={label}
      optional={optional}
      errorMessage={errorMessage}
      description={description}
    >
      <Tooltip title={tooltipContent}>
        <span onClick={onClick}>
          <Autocomplete
            disabled={disabled}
            size="large"
            placeholder={label}
            options={suggestions}
            loading={isLoading}
            value={fieldValue || defaultValue}
            onChange={(_, value) => {
              setValue(field.key, value);
              if (!optional) {
                formMethods.clearErrors(field.key);
              }
            }}
            onInputChange={handleInputChange}
            customEmptyMessage={getEmptyMessage()}
            freeSolo={field.schema_info?.items === 'string'}
            multiple={field.schema_info?.type === 'array'}
            listboxMaxHeight={240}
            listboxTitle={'Suggestions'}
          />
        </span>
      </Tooltip>
    </FormField>
  );
};

export default JiraDynamicFieldAutocomplete;
